import React, { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Pencil, Trash2, Check, X } from "lucide-react";

const SignaturePad = ({ 
  label = "İmza",
  signatureData = null,
  onSave,
  disabled = false,
  required = false,
  size = "medium" // small, medium, large
}) => {
  const sigCanvas = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hasSignature, setHasSignature] = useState(!!signatureData);
  const [currentSignature, setCurrentSignature] = useState(signatureData);

  const sizes = {
    small: { width: 300, height: 100 },
    medium: { width: 400, height: 150 },
    large: { width: 500, height: 200 }
  };

  const { width, height } = sizes[size];

  useEffect(() => {
    setCurrentSignature(signatureData);
    setHasSignature(!!signatureData);
  }, [signatureData]);

  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleSave = () => {
    if (sigCanvas.current) {
      if (sigCanvas.current.isEmpty()) {
        setCurrentSignature(null);
        setHasSignature(false);
        if (onSave) onSave(null);
      } else {
        const dataUrl = sigCanvas.current.toDataURL("image/png");
        setCurrentSignature(dataUrl);
        setHasSignature(true);
        if (onSave) onSave(dataUrl);
      }
    }
    setIsOpen(false);
  };

  const handleRemove = () => {
    setCurrentSignature(null);
    setHasSignature(false);
    if (onSave) onSave(null);
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Pencil className="w-4 h-4" />
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      
      {/* Signature Preview or Placeholder */}
      <div 
        className={`border-2 border-dashed rounded-lg p-2 flex items-center justify-center ${
          hasSignature ? "border-green-300 bg-green-50" : "border-gray-300 bg-gray-50"
        }`}
        style={{ minHeight: height / 2 }}
      >
        {currentSignature ? (
          <div className="relative w-full">
            <img 
              src={currentSignature} 
              alt="İmza" 
              className="max-h-20 mx-auto"
              style={{ maxWidth: "100%" }}
            />
            {!disabled && (
              <button
                type="button"
                onClick={handleRemove}
                className="absolute top-0 right-0 p-1 bg-red-100 hover:bg-red-200 rounded-full"
              >
                <X className="w-4 h-4 text-red-600" />
              </button>
            )}
          </div>
        ) : (
          <p className="text-gray-400 text-sm">İmza yok</p>
        )}
      </div>

      {/* Sign Button */}
      {!disabled && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button 
              type="button"
              variant="outline" 
              size="sm"
              className="w-full border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F3EE]"
              data-testid={`signature-btn-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Pencil className="w-4 h-4 mr-2" />
              {hasSignature ? "İmzayı Değiştir" : "İmza At"}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5" />
                {label}
              </DialogTitle>
              <DialogDescription>
                Aşağıdaki alana parmağınızla veya mouse ile imzanızı atın
              </DialogDescription>
            </DialogHeader>
            
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  width: width,
                  height: height,
                  className: "signature-canvas w-full touch-none",
                  style: { 
                    width: "100%", 
                    height: height,
                    touchAction: "none"
                  }
                }}
                backgroundColor="white"
                penColor="#1A4D2E"
                dotSize={1}
                minWidth={1}
                maxWidth={2.5}
              />
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              Mobil cihazlarda parmağınızı kullanabilirsiniz
            </p>

            <DialogFooter className="flex gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleClear}
                className="flex-1 sm:flex-none"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Temizle
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="flex-1 sm:flex-none"
              >
                <X className="w-4 h-4 mr-2" />
                İptal
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                className="flex-1 sm:flex-none bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
              >
                <Check className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SignaturePad;
